import { usePathname, useSearchParams } from 'next/navigation'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

import type { AnalyticsContextProps } from '../types/analytics'
import {
  captureEvent,
  capturePageview,
  identify,
  identifyWorkspace,
  initializePosthog,
  setUserProperty
} from '../utils/analytics-service'
import { tokenUtils } from '../utils/token-utils'
import { createGenericContext } from './generic-context'

const [useAnalytics, AnalyticsContextProvider] = createGenericContext<AnalyticsContextProps>()

interface AnalyticsProviderProps {
  children: ReactNode
}

const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const userID = tokenUtils.getUserID() || undefined

  useEffect(() => {
    // Initialize PostHog only on client-side
    if (typeof window !== 'undefined') {
      initializePosthog()
    }
  }, [])

  useEffect(() => {
    if (userID && typeof window !== 'undefined') {
      identify(userID)
    }
  }, [userID])

  // Automatic page view tracking
  useEffect(() => {
    if (pathname && typeof window !== 'undefined') {
      const path = searchParams.size > 0 ? `${pathname}?${searchParams.toString()}` : pathname

      capturePageview(path)
    }
  }, [pathname, searchParams])

  const value: AnalyticsContextProps = {
    captureEvent: typeof window === 'undefined' ? () => {} : captureEvent,
    capturePageview: typeof window === 'undefined' ? () => {} : capturePageview,
    identify: typeof window === 'undefined' ? () => {} : identify,
    identifyWorkspace: typeof window === 'undefined' ? () => {} : identifyWorkspace,
    setUserProperty: typeof window === 'undefined' ? () => {} : setUserProperty
  }

  return <AnalyticsContextProvider value={value}>{children}</AnalyticsContextProvider>
}

export { AnalyticsProvider, useAnalytics }
